/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React from 'react';

import { jsx, cssMap as unboundedCssMap } from '@compiled/react';

import { useConfluenceDependencies } from '../../../controllers/use-confluence-dependencies';
import { CustomImageUploadRenderer } from '../custom-image-upload-renderer';
import { getImageTypeToShow } from '../utils';

export interface ImageRendererProps {
	contentId: string;
	imageSrc?: string;
	imageAlt?: string;
	shouldShowExternalSiteImage?: boolean;
}

const unboundedStyles = unboundedCssMap({
	unsplashImage: {
		height: '100%',
		width: '100%',
		objectFit: 'cover',
	},
});

export const ImageRenderer = ({
	contentId,
	imageSrc,
	imageAlt,
	shouldShowExternalSiteImage,
}: ImageRendererProps) => {
	const { experienceSuccessComponent } = useConfluenceDependencies();
	if (!imageSrc) {
		return null;
	}
	const { showUnsplashImage, showCustomUploadedImage, showExternalSiteImage } = getImageTypeToShow({
		imageSrc,
	});

	const ExperienceSuccessComponent = experienceSuccessComponent || React.Fragment;

	if (showUnsplashImage || (shouldShowExternalSiteImage && showExternalSiteImage)) {
		return (
			<>
				<img
					data-testid="unsplash-img-element"
					alt={imageAlt}
					src={imageSrc}
					css={unboundedStyles.unsplashImage}
				/>
				<ExperienceSuccessComponent />
			</>
		);
	} else if (showCustomUploadedImage) {
		return (
			<CustomImageUploadRenderer
				contentId={contentId}
				imageSrc={imageSrc}
				imageAltText={imageAlt}
				shouldShowErrorMessage
			/>
		);
	}

	// Fallback if nothing matches
	return <ExperienceSuccessComponent />;
};
